import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import RefreshIcon from "@mui/icons-material/Refresh";
import ScienceOutlinedIcon from "@mui/icons-material/ScienceOutlined";
import Tooltip from "@mui/material/Tooltip";

const SURFACE_FORM_LABELS = {
  H: "Hummocky, Knoll & Kettle",
  I: "Inclined & Dissected",
  L: "Level",
  R: "Rolling",
  U: "Undulating",
};

const SLOPE_CLASS_LABELS = {
  A: "A (0 - 0.5%)",
  B: "B (0.5 - 2.0%)",
  C: "C (2.0 - 5.0%)",
  D: "D (5.0 - 9.0%)",
  E: "E (9.0 - 15.0%)",
  F: "F (15.0 - 30.0%)",
};

const PROFILE_METRICS = [
  { key: "sand", shortLabel: "Sand", digits: 1 },
  { key: "silt", shortLabel: "Silt", digits: 1 },
  { key: "clay", shortLabel: "Clay", digits: 1 },
  { key: "ph_cacl2", shortLabel: "pH CaCl2", digits: 2 },
  { key: "soil_organic_carbon", shortLabel: "SOC", digits: 2 },
  { key: "cation_exchange_capacity", shortLabel: "CEC", digits: 1 },
  { key: "bulk_density", shortLabel: "Bulk density", digits: 2 },
];

const DEPTH_ORDER = [
  "0-5 cm",
  "5-15 cm",
  "15-30 cm",
  "30-60 cm",
  "60-100 cm",
];

function formatValue(value, digits = 1) {
  if (value === null || value === undefined || value === "") return "-";
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric.toFixed(digits) : "-";
}

function formatCoordinate(value) {
  if (value === null || value === undefined || value === "") return "-";
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric.toFixed(6) : "-";
}

function formatSuggestion(value, labels) {
  if (value == null || String(value).trim() === "") return "Not suggested";
  return labels?.[value] || String(value);
}

function readableSourceStatus(status) {
  if (status === "ok") return "Available";
  if (status === "partial") return "Partial";
  if (status === "not_covered") return "Outside coverage";
  return "Unavailable";
}

function propertyValueAtDepth(properties, propertyKey, depth) {
  return properties?.[propertyKey]?.values?.find(
    (entry) => entry.depth === depth
  );
}

function collectDepths(properties) {
  const found = new Set(
    Object.values(properties || {}).flatMap((property) =>
      (property?.values || []).map((entry) => entry.depth).filter(Boolean)
    )
  );
  return [...found].sort((left, right) => {
    const leftIndex = DEPTH_ORDER.indexOf(left);
    const rightIndex = DEPTH_ORDER.indexOf(right);
    if (leftIndex === -1 && rightIndex === -1) return left.localeCompare(right);
    if (leftIndex === -1) return 1;
    if (rightIndex === -1) return -1;
    return leftIndex - rightIndex;
  });
}

function SoilValue({ label, value, unit = "" }) {
  return (
    <div className="min-w-0 bg-[#fcfcfa] px-2 py-2.5">
      <dt className="text-[10px] font-bold uppercase text-slate-400">{label}</dt>
      <dd className="mt-1 break-words text-sm font-bold text-slate-900">
        {value}
        {value !== "-" && unit ? (
          <span className="ml-1 text-[10px] font-semibold text-slate-500">{unit}</span>
        ) : null}
      </dd>
    </div>
  );
}

export default function FieldSoilEstimatePanel({
  enrichment,
  reason = null,
  loading = false,
  error = "",
  canRefresh = false,
  onRefresh,
  compact = false,
}) {
  const normalized = enrichment?.normalized_data || enrichment || null;
  const predictions = normalized?.predictions || {};
  const properties = predictions.properties || {};
  const surface = predictions.surface_0_15_cm || {};
  const suggestions = normalized?.suggestions || {};
  const sourceStatus =
    normalized?.source_status || enrichment?.source_status || {};
  const sourceVersions =
    normalized?.source_versions || enrichment?.source_versions || {};
  const survey = normalized?.survey || {};
  const component = survey.dominant_component || {};
  const provenance = normalized?.provenance || {};
  const coordinate = normalized?.coordinate || {
    latitude: enrichment?.centroid_latitude,
    longitude: enrichment?.centroid_longitude,
  };
  const warnings = Array.isArray(normalized?.warnings)
    ? normalized.warnings.filter(Boolean)
    : [];
  const depths = collectDepths(properties);
  const refreshedAt = enrichment?.retrieved_at
    ? new Date(enrichment.retrieved_at).toLocaleDateString()
    : "";
  const sourceEntries = [
    ["Detailed survey", "dss"],
    ["Soil landscapes", "slc"],
    ["100 m model grids", "grids"],
  ];

  return (
    <section
      aria-label="Official soil estimate"
      className={`border-y border-slate-200 bg-white ${
        compact ? "my-4 px-1 py-4" : "px-4 py-4 md:px-5"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-[#e9f6ed] text-[#188038]">
            <ScienceOutlinedIcon fontSize="small" />
          </span>
          <div className="min-w-0">
            <p className="text-[11px] font-bold uppercase text-slate-400">
              Official soil estimate
            </p>
            <h3 className="mt-1 text-base font-bold text-slate-900">
              Field boundary profile
            </h3>
            <p className="mt-1 text-xs leading-5 text-slate-500">
              Computed at the boundary centroid from official Canadian sources.
            </p>
          </div>
        </div>
        {canRefresh ? (
          <Tooltip title="Refresh from official sources">
            <span>
              <button
                aria-label="Refresh official soil estimate"
                className="flex h-9 w-9 items-center justify-center rounded-md border border-slate-300 text-slate-600 hover:bg-slate-50 disabled:cursor-wait disabled:opacity-50"
                disabled={loading}
                onClick={onRefresh}
                type="button"
              >
                <RefreshIcon className={loading ? "animate-spin" : ""} fontSize="small" />
              </button>
            </span>
          </Tooltip>
        ) : null}
      </div>

      {error ? (
        <p className="mt-3 rounded-md bg-red-50 px-3 py-2 text-xs font-semibold text-red-700">
          {error}
        </p>
      ) : null}

      {normalized && reason === "field_boundary_required" ? <p className="mt-3 text-xs text-amber-800">Add field boundaries before generating a saved official soil estimate. Coordinates can still be used for analysis.</p> : null}
      {!normalized ? (
        <div className="mt-3 flex items-start gap-2 text-xs leading-5 text-slate-500">
          <InfoOutlinedIcon className="mt-0.5" fontSize="inherit" />
          <span>
            {loading
              ? "Retrieving official soil sources..."
              : reason === "field_boundary_required"
                ? "Add field boundaries to generate an official soil estimate. Latitude and longitude alone can support analysis; manual soil-test records can still be saved."
                : "No estimate has been generated for this boundary."}
          </span>
        </div>
      ) : (
        <>
          <p className="mt-4 text-[10px] font-bold uppercase text-slate-400">
            Modelled surface texture (0 - 15 cm)
          </p>
          <dl className="mt-1.5 grid grid-cols-3 gap-px overflow-hidden rounded-md border border-slate-200 bg-slate-200">
            {[
              ["Sand", surface.sand],
              ["Silt", surface.silt],
              ["Clay", surface.clay],
            ].map(([label, value]) => (
              <SoilValue
                key={label}
                label={label}
                unit="%"
                value={formatValue(value)}
              />
            ))}
          </dl>

          <p className="mt-4 text-[10px] font-bold uppercase text-slate-400">
            Modelled surface properties (0 - 15 cm)
          </p>
          <dl className="mt-1.5 grid grid-cols-2 gap-px overflow-hidden rounded-md border border-slate-200 bg-slate-200">
            <SoilValue label="pH (CaCl2)" value={formatValue(surface.ph_cacl2, 2)} />
            <SoilValue
              label="Soil organic carbon"
              unit="%"
              value={formatValue(surface.soil_organic_carbon, 2)}
            />
            <SoilValue
              label="Cation exchange capacity"
              unit="meq/100g"
              value={formatValue(surface.cation_exchange_capacity, 1)}
            />
            <SoilValue
              label="Bulk density"
              unit="g/cm3"
              value={formatValue(surface.bulk_density, 2)}
            />
          </dl>

          <div className="mt-4 border-t border-slate-100 pt-3">
            <p className="text-[10px] font-bold uppercase text-slate-400">
              Computed geography and classification
            </p>
            <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
              <div>
                <dt className="text-slate-400">Ecodistrict</dt>
                <dd className="font-semibold text-slate-800">
                  {suggestions.ecodistrict || "Not covered"}
                </dd>
              </div>
              <div>
                <dt className="text-slate-400">SLC polygon</dt>
                <dd className="font-semibold text-slate-800">
                  {suggestions.slc_polygon || "Not covered"}
                </dd>
              </div>
              <div>
                <dt className="text-slate-400">SLC soil code</dt>
                <dd className="font-semibold text-slate-800">
                  {suggestions.soil_code || "Not returned"}
                </dd>
              </div>
              <div>
                <dt className="text-slate-400">Great Group</dt>
                <dd className="font-semibold text-slate-800">
                  {predictions.great_group?.label || "Not returned"}
                </dd>
              </div>
            </dl>
          </div>

          <div className="mt-3 border-t border-slate-100 pt-3 text-xs">
            <p className="text-[10px] font-bold uppercase text-slate-400">
              Detailed survey
            </p>
            <p className="mt-1 font-semibold text-slate-800">
              {survey.dominant_soil_name || survey.map_unit || "Outside survey coverage"}
              {survey.dominant_percent != null
                ? ` (${survey.dominant_percent}%)`
                : ""}
            </p>
            <p className="mt-1 text-slate-500">
              Map unit {survey.map_unit || "not returned"}
              {survey.component_count != null
                ? `, ${survey.component_count} component${
                    survey.component_count === 1 ? "" : "s"
                  }`
                : ""}
            </p>
          </div>

          <details className="group mt-3 border-y border-slate-200">
            <summary className="flex cursor-pointer list-none items-center justify-between gap-3 py-3 text-xs font-bold text-slate-800 [&::-webkit-details-marker]:hidden">
              <span>Full soil profile</span>
              <ExpandMoreIcon
                aria-hidden="true"
                className="text-slate-500 transition-transform group-open:rotate-180"
                fontSize="small"
              />
            </summary>
            <div className="border-t border-slate-100 pb-4 pt-3">
              <section aria-labelledby="soil-form-suggestions-heading">
                <h4
                  className="text-[10px] font-bold uppercase text-slate-400"
                  id="soil-form-suggestions-heading"
                >
                  Reviewable form suggestions
                </h4>
                <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                  <div>
                    <dt className="text-slate-400">Surface form</dt>
                    <dd className="font-semibold text-slate-800">
                      {formatSuggestion(
                        suggestions.surface_form,
                        SURFACE_FORM_LABELS
                      )}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-slate-400">Slope class</dt>
                    <dd className="font-semibold text-slate-800">
                      {formatSuggestion(
                        suggestions.slope_class,
                        SLOPE_CLASS_LABELS
                      )}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-slate-400">Surface texture</dt>
                    <dd className="font-semibold text-slate-800">
                      {formatSuggestion(suggestions.surface_soil_texture)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-slate-400">Soil type</dt>
                    <dd className="font-semibold text-slate-800">
                      {formatSuggestion(suggestions.soil_type)}
                    </dd>
                  </div>
                </dl>
              </section>

              {depths.length ? (
                <section
                  aria-labelledby="soil-depth-profile-heading"
                  className="mt-4 border-t border-slate-100 pt-3"
                >
                  <h4
                    className="text-[10px] font-bold uppercase text-slate-400"
                    id="soil-depth-profile-heading"
                  >
                    Modelled depth profile
                  </h4>
                  <div className="mt-2 divide-y divide-slate-200 border-y border-slate-200">
                    {depths.map((depth) => (
                      <div className="py-3" key={depth}>
                        <p className="text-xs font-bold text-slate-900">{depth}</p>
                        <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-2">
                          {PROFILE_METRICS.map((metric) => {
                            const entry = propertyValueAtDepth(
                              properties,
                              metric.key,
                              depth
                            );
                            return (
                              <div className="min-w-0" key={metric.key}>
                                <dt className="text-[10px] font-semibold text-slate-400">
                                  {metric.shortLabel}
                                </dt>
                                <dd className="break-words text-xs font-semibold text-slate-700">
                                  {formatValue(entry?.value, metric.digits)}
                                  {entry?.value != null && entry?.unit
                                    ? ` ${entry.unit}`
                                    : ""}
                                </dd>
                              </div>
                            );
                          })}
                        </dl>
                      </div>
                    ))}
                  </div>
                </section>
              ) : null}

              <section
                aria-labelledby="soil-lookup-heading"
                className="mt-4 border-t border-slate-100 pt-3"
              >
                <h4
                  className="text-[10px] font-bold uppercase text-slate-400"
                  id="soil-lookup-heading"
                >
                  Lookup and provenance
                </h4>
                <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                  <div className="col-span-2">
                    <dt className="text-slate-400">Boundary centroid</dt>
                    <dd className="break-all font-semibold text-slate-800">
                      {formatCoordinate(coordinate.latitude)}, {" "}
                      {formatCoordinate(coordinate.longitude)}
                    </dd>
                  </div>
                  {component.CMP_ID ? (
                    <div className="col-span-2">
                      <dt className="text-slate-400">Survey component ID</dt>
                      <dd className="break-all font-semibold text-slate-800">
                        {component.CMP_ID}
                      </dd>
                    </div>
                  ) : null}
                </dl>

                <div className="mt-3 divide-y divide-slate-100 border-y border-slate-100">
                  {sourceEntries.map(([label, key]) => {
                    const source = provenance[key] || {};
                    const status = sourceStatus[key] || {};
                    return (
                      <div className="py-2.5 text-xs" key={key}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="font-semibold text-slate-800">{label}</p>
                            <p className="mt-0.5 break-words text-slate-500">
                              {source.lookup_method || "Official source lookup"}
                            </p>
                          </div>
                          <span className="shrink-0 font-semibold text-slate-600">
                            {readableSourceStatus(status.status)}
                          </span>
                        </div>
                        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] text-slate-500">
                          {sourceVersions[key] ? (
                            <span>Version {sourceVersions[key]}</span>
                          ) : null}
                          {source.scale ? <span>Scale {source.scale}</span> : null}
                          {source.source_url ? (
                            <a
                              className="inline-flex items-center gap-1 font-semibold text-[#1769aa] hover:underline"
                              href={source.source_url}
                              rel="noreferrer"
                              target="_blank"
                            >
                              Source
                              <OpenInNewIcon fontSize="inherit" />
                            </a>
                          ) : null}
                        </div>
                        {status.message ? (
                          <p className="mt-1 text-amber-700">{status.message}</p>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </section>

              {warnings.length ? (
                <div className="mt-3 rounded-md bg-amber-50 px-3 py-2 text-xs leading-5 text-amber-800">
                  {warnings.map((warning, index) => (
                    <p key={`${String(warning)}-${index}`}>{String(warning)}</p>
                  ))}
                </div>
              ) : null}

              <div className="mt-3 flex items-start gap-2 text-xs leading-5 text-slate-500">
                <InfoOutlinedIcon className="mt-0.5 shrink-0" fontSize="inherit" />
                <p>
                  Grid values are modelled point estimates, not laboratory
                  measurements or whole-field averages. Soil organic carbon is not
                  the same measurement as laboratory organic matter.
                </p>
              </div>
            </div>
          </details>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <div className="flex flex-wrap gap-2 text-[10px] font-bold uppercase text-slate-500">
              <span>SLC: {readableSourceStatus(sourceStatus.slc?.status)}</span>
              <span>Grids: {readableSourceStatus(sourceStatus.grids?.status)}</span>
              <span>Survey: {readableSourceStatus(sourceStatus.dss?.status)}</span>
              {refreshedAt ? <span>Updated {refreshedAt}</span> : null}
              {enrichment?.is_stale ? (
                <span className="text-amber-700">Boundary changed</span>
              ) : null}
            </div>
          </div>
        </>
      )}
    </section>
  );
}

export { collectDepths, formatValue };
